import { useState, useEffect, useRef } from 'react';
import { useCart, clearCartCompletely } from '../context/CartContext';
import api from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackCartEvent } from '../services/analytics';

const normalizeDeliveryInfo = (rawDelivery) => {
  if (!rawDelivery || typeof rawDelivery !== 'object') {
    return null;
  }

  return {
    ...rawDelivery,
    recipientName: rawDelivery.recipientName ?? rawDelivery.recipient_name ?? '',
    phone: rawDelivery.phone ?? '',
    address:
      rawDelivery.address ??
      rawDelivery.deliveryAddress ??
      rawDelivery.delivery_address ??
      '',
  };
};

const getStoredCheckoutDelivery = () => {
  try {
    if (typeof window === 'undefined') {
      return null;
    }

    const stored = window.localStorage.getItem('checkoutDelivery');
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const Checkout = () => {
  const location = useLocation();
  const state = location.state;
  const navigate = useNavigate();
  const {
    state: { items },
    dispatch,
  } = useCart();
  const cartItems = Array.isArray(items) ? items : [];

  const [draftOrder, setDraftOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [delivery, setDelivery] = useState(() => state?.delivery || getStoredCheckoutDelivery());
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [step, setStep] = useState('payment');
  const [error, setError] = useState(null);
  const isCompletedRef = useRef(false);
  const total = draftOrder?.totalAmount || cartItems.reduce((sum, item) => sum + item.product.price * item.qty, 0);
  const displayDelivery = normalizeDeliveryInfo(
    delivery || draftOrder?.deliveryInfo || draftOrder?.delivery
  );

  useEffect(() => {
    return () => {
      if (!isCompletedRef.current && cartItems.length > 0) {
        trackCartEvent({
          action: 'checkout',
          status: 'abandoned',
          cartValue: draftOrder?.totalAmount || total,
          metadata: { current_step: step },
        });
      }
    };
  }, [cartItems.length, draftOrder, step, total]);

  useEffect(() => {
    let isMounted = true;

    const initializeOrder = async () => {
      try {
        const orderToken = localStorage.getItem('activeOrderToken');
        const headers = orderToken ? { 'x-order-token': orderToken } : {};

        if (cartItems.length > 0) {
          const normalizedDraftDelivery = normalizeDeliveryInfo(
            state?.delivery || delivery || getStoredCheckoutDelivery()
          );
          const draftPayload = {
            items: cartItems.map(({ product, qty }) => ({
              productId: product._id ?? product.id,
              quantity: qty,
              price: product.price,
            })),
            totalAmount: cartItems.reduce((sum, item) => sum + item.product.price * item.qty, 0),
            delivery: normalizedDraftDelivery,
          };

          const response = await api.post('/orders/draft', draftPayload, { headers });
          const nextDraft = response.data;

          if (!isMounted) {
            return;
          }

          setDraftOrder(nextDraft);
          if (nextDraft.deliveryInfo) {
            setDelivery(nextDraft.deliveryInfo);
          }

          trackCartEvent({
            action: 'checkout',
            status: 'started',
            cartValue: draftPayload.totalAmount,
            metadata: { item_count: cartItems.length },
          });

          if (nextDraft.orderToken) {
            localStorage.setItem('activeOrderToken', nextDraft.orderToken);
            localStorage.setItem(`orderToken:${nextDraft.id}`, nextDraft.orderToken);
          }

          return;
        }

        if (orderToken) {
          try {
            const { data: activeOrder } = await api.get('/orders/active', { headers });

            if (isMounted && activeOrder?.id) {
              setDraftOrder(activeOrder);
              if (activeOrder.deliveryInfo) {
                setDelivery(activeOrder.deliveryInfo);
              }
            }
          } catch {
          }
        }
      } catch (err) {
        console.error('Failed to initialize order:', err);
        const backendMessage =
          err?.response?.data?.message || err?.response?.data?.error || err?.message;
        let message =
          'We could not pre-initialize your order. You can still try placing it, but please check your details carefully.';

        if (backendMessage) {
          message = `${message} (${backendMessage})`;
        }

        if (isMounted) {
          setError(message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void initializeOrder();

    return () => {
      isMounted = false;
    };
  }, [cartItems, delivery, state?.delivery]);

  useEffect(() => {
    if (!loading && cartItems.length === 0 && !draftOrder?.id) {
      navigate('/cart', { replace: true });
    }
  }, [loading, cartItems.length, draftOrder, navigate]);

  useEffect(() => {
    if (!loading && !displayDelivery) {
      navigate('/delivery', { replace: true });
    }
  }, [loading, displayDelivery, navigate]);

  if (loading) {
    return (
      <main className="px-4 md:px-10 max-w-2xl mx-auto bg-light-primary dark:bg-brand-dark min-h-screen transition-colors duration-slow">
        <p className="text-light-text-primary dark:text-brand-text-primary">Loading checkout...</p>
      </main>
    );
  }

  if (!displayDelivery) {
    return null;
  }

  const handlePaymentSelection = (method) => {
    setSelectedPaymentMethod(method);
    setStep('confirm');
  };

  const handleConfirmOrder = async () => {
    if (!selectedPaymentMethod) {
      alert('Please select a payment method');
      setStep('payment');
      return;
    }

    if (submitting) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (!displayDelivery) {
        throw new Error('Delivery details are missing. Please return to the delivery step and try again.');
      }

      if (!draftOrder?.id && cartItems.length === 0) {
        throw new Error('Your cart is empty. Please add items before checking out.');
      }

      const orderToken = localStorage.getItem('activeOrderToken');
      const headers = orderToken ? { 'x-order-token': orderToken } : {};
      headers['x-idempotency-key'] = `confirm-${draftOrder?.id || 'new'}-${Date.now()}`;

      if (draftOrder?.id) {
        await api.patch(
          `/orders/${draftOrder.id}/progress`,
          {
            paymentMethod: selectedPaymentMethod,
            delivery: displayDelivery,
          },
          { headers }
        );

        if (selectedPaymentMethod === 'mobile_money') {
          const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

          if (isMobile) {
            window.location.href = 'tel:*185%23';
          } else {
            alert('Please dial *185# manually on your mobile phone to complete the payment.');
          }

          trackCartEvent({
            action: 'checkout',
            status: 'succeeded',
            cartValue: total,
            metadata: { payment_method: selectedPaymentMethod },
          });

          isCompletedRef.current = true;
          clearCartCompletely();
          dispatch({ type: 'CLEAR' });

          navigate(`/upload-proof/${draftOrder.id}`, {
            state: { paymentMethod: 'mobile_money', orderToken },
          });
          return;
        }

        const res = await api.post(
          `/orders/${draftOrder.id}/submit`,
          {},
          { headers }
        );
        const id = res.data?.id || res.data?._id;

        if (!id) {
          throw new Error('Order submission did not return a valid order ID. Please try again.');
        }

        if (orderToken) {
          localStorage.setItem(`orderToken:${id}`, orderToken);
        }

        trackCartEvent({
          action: 'checkout',
          status: 'succeeded',
          cartValue: draftOrder?.totalAmount || total,
          metadata: { payment_method: selectedPaymentMethod },
        });

        isCompletedRef.current = true;
        clearCartCompletely();
        dispatch({ type: 'CLEAR' });

        navigate(`/confirmation/${id}`, {
          state: { paymentMethod: selectedPaymentMethod, orderToken },
        });
        return;
      }

      const payload = {
        items: cartItems.map(({ product, qty }) => ({
          productId: product._id ?? product.id,
          quantity: qty,
          price: product.price,
        })),
        paymentMethod: selectedPaymentMethod,
        totalAmount: total,
        delivery: displayDelivery,
      };
      const res = await api.post('/orders', payload, { headers });
      const id = res.data?.id || res.data?._id;
      const newOrderToken = res.data?.orderToken;

      if (!id) {
        throw new Error('Order was not created successfully. Please try again.');
      }

      if (newOrderToken) {
        localStorage.setItem(`orderToken:${id}`, newOrderToken);
      }

      trackCartEvent({
        action: 'checkout',
        status: 'succeeded',
        cartValue: total,
        metadata: { payment_method: selectedPaymentMethod },
      });

      isCompletedRef.current = true;
      clearCartCompletely();
      dispatch({ type: 'CLEAR' });

      if (selectedPaymentMethod === 'mobile_money') {
        const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

        if (isMobile) {
          window.location.href = 'tel:*185%23';
        } else {
          alert('Please dial *185# manually on your mobile phone to complete the payment.');
        }

        navigate(`/upload-proof/${id}`, {
          state: {
            paymentMethod: 'mobile_money',
            orderToken: newOrderToken,
          },
        });
      } else {
        navigate(`/confirmation/${id}`, {
          state: {
            paymentMethod: selectedPaymentMethod,
            orderToken: newOrderToken,
          },
        });
      }
    } catch (err) {
      console.error('Failed to confirm order:', err);

      trackCartEvent({
        action: 'checkout',
        status: 'failed',
        cartValue: draftOrder?.totalAmount || total,
        errorMessage: err.message,
        metadata: { payment_method: selectedPaymentMethod },
      });

      let message = 'Could not confirm order. Please try again.';
      if (err?.response) {
        const status = err.response.status;
        const data = err.response.data || {};
        if (status === 400) {
          message = data.message || data.error || 'Some of your order details are invalid. Please review and try again.';
        } else if (status === 401) {
          message = 'Session expired. Please log in again to place your order.';
        } else if (status === 409) {
          message =
            data.message ||
            data.error ||
            'One or more items went out of stock while you were checking out. Another customer purchased them first. Please review your cart; availability has changed.';
        } else if (status === 500) {
          message = 'Server error while creating your order. Please try again later.';
        } else {
          message = data.message || data.error || message;
        }
      } else if (err?.request) {
        message = 'Cannot reach the server. Please check your internet connection and try again.';
      } else if (err?.message) {
        message = err.message;
      }

      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToPayment = () => {
    setStep('payment');
    setSelectedPaymentMethod(null);
  };

  const orderItemsForDisplay = draftOrder?.items && draftOrder.items.length > 0
    ? (draftOrder.items || []).map((item) => ({
        product: item.product || {
          _id: item.product_id,
          name: item.product_name || 'Product',
          price: item.price,
        },
        qty: item.quantity,
      }))
    : cartItems;

  return (
    <main className="px-4 md:px-10 max-w-2xl mx-auto bg-light-primary dark:bg-brand-dark min-h-screen transition-colors duration-slow">
      <h1 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-6 pt-[16px] md:pt-[24px] text-light-text-primary dark:text-brand-text-primary">Checkout</h1>
      {error && (
        <div className="mb-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded text-sm sm:text-base">
          <span className="font-bold mr-1">Error:</span>
          <span>{error}</span>
        </div>
      )}
      <div className="mb-6 sm:mb-8">
        <h2 className="font-semibold text-base sm:text-lg mb-2 text-light-text-primary dark:text-brand-text-primary">Delivery To</h2>
        <p className="mb-2 text-sm sm:text-base text-light-text-secondary dark:text-brand-text-secondary">{displayDelivery.recipientName} — {displayDelivery.phone}</p>
        <p className="mb-4 sm:mb-6 text-sm sm:text-base text-light-text-secondary dark:text-brand-text-secondary">{displayDelivery.address}</p>
        <h2 className="font-semibold text-base sm:text-lg mb-2 text-light-text-primary dark:text-brand-text-primary">Order Summary</h2>
        <ul className="space-y-2">
          {orderItemsForDisplay.map(({ product, qty }) => (
            <li key={product._id || product.id} className="flex justify-between text-sm sm:text-base text-light-text-secondary dark:text-brand-text-secondary">
              <span>
                {product.name} x{qty}
              </span>
              <span>UGX {(product.price * qty).toLocaleString()}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-between font-bold mt-4 text-base sm:text-lg text-light-text-primary dark:text-brand-text-primary">
          <span>Total</span>
          <span>UGX {total.toLocaleString()}</span>
        </div>
      </div>
      {step === 'payment' && (
        <>
          <h2 className="font-semibold text-base sm:text-lg mb-4 text-light-text-primary dark:text-brand-text-primary">Choose Payment Method</h2>
          <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2">
            <button
              onClick={() => handlePaymentSelection('mobile_money')}
              className="bg-yellow-500 dark:bg-yellow-600 text-white py-4 rounded hover:bg-yellow-600 dark:hover:bg-yellow-700 transition min-h-[56px] text-base sm:text-lg font-medium"
            >
              Mobile Money (MTN / Airtel)
            </button>
            <button
              onClick={() => handlePaymentSelection('cash_on_delivery')}
              className="bg-gray-800 dark:bg-gray-700 text-white py-4 rounded hover:bg-gray-900 dark:hover:bg-gray-800 transition min-h-[56px] text-base sm:text-lg font-medium"
            >
              Cash on Delivery
            </button>
          </div>
        </>
      )}

      {step === 'confirm' && (
        <div className="bg-light-secondary dark:bg-gray-800 border border-light-border-default dark:border-gray-700 rounded-lg p-4 sm:p-6">
          <h2 className="font-semibold text-lg sm:text-xl mb-4 text-light-text-primary dark:text-brand-text-primary">Confirm Your Order</h2>

          <div className="mb-4 p-3 bg-light-primary dark:bg-gray-900 rounded border border-light-border-default dark:border-gray-700">
            <p className="text-sm text-light-text-muted dark:text-brand-text-muted">Payment Method:</p>
            <p className="font-medium capitalize text-base text-light-text-primary dark:text-brand-text-primary">
              {selectedPaymentMethod?.replace('_', ' ')}
            </p>
          </div>

          <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              {selectedPaymentMethod === 'mobile_money'
                ? 'After confirming, you will be prompted to complete payment via Mobile Money.'
                : 'Payment will be collected upon delivery.'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button
              onClick={handleBackToPayment}
              disabled={submitting}
              className="flex-1 bg-light-elevated dark:bg-brand-elevated text-light-text-primary dark:text-brand-text-primary py-3 rounded hover:bg-light-secondary dark:hover:bg-brand-surface border border-transparent dark:border-brand-border-default transition disabled:opacity-50 min-h-[48px] text-base font-medium focus:outline-none focus:ring-2 focus:ring-light-accent-red dark:focus:ring-brand-accent-red"
            >
              Back
            </button>
            <button
              onClick={handleConfirmOrder}
              disabled={submitting}
              className="flex-1 bg-green-600 dark:bg-green-700 text-white py-3 rounded hover:bg-green-700 dark:hover:bg-green-800 transition disabled:opacity-50 min-h-[48px] text-base font-semibold"
            >
              {submitting ? 'Confirming...' : 'Confirm Order'}
            </button>
          </div>

          <p className="text-xs text-light-text-muted dark:text-brand-text-muted mt-4 text-center">
            By confirming, you agree to place this order. Stock will be reserved for you.
          </p>
        </div>
      )}
    </main>
  );
};

export default Checkout;
